import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { referralsAPI } from '../api';
import { ChevronLeft, Mail, Lock, Eye, EyeOff, User, Phone, ShieldCheck, Gift } from 'lucide-react';

const PENDING_KEY = 'lunora_auth_redirect_v1';
const PENDING_TTL_MS = 30 * 60 * 1000;

const CustomerAuth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Persist the pending destination so it survives forgot-password detours, tab reloads, etc.
  const pending = useMemo(() => {
    if (location.state?.redirectTo) {
      const p = { redirectTo: location.state.redirectTo, redirectState: location.state.redirectState, at: Date.now() };
      try { sessionStorage.setItem(PENDING_KEY, JSON.stringify(p)); } catch {/* noop */}
      return p;
    }
    try {
      const p = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
      return p && Date.now() - p.at < PENDING_TTL_MS ? p : null;
    } catch { return null; }
  }, [location.state]);
  const redirectTo = pending?.redirectTo;
  const goAfterAuth = () => {
    try { sessionStorage.removeItem(PENDING_KEY); } catch {/* noop */}
    return redirectTo
      ? navigate(redirectTo, { replace: true, state: pending.redirectState })
      : navigate('/landing');
  };
  const { login, register, user } = useAuth();
  const [mode, setMode] = useState(searchParams.get('ref') ? 'signup' : 'login');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '', email: '', password: '', phone: '',
    referral_code: (searchParams.get('ref') || '').toUpperCase(),
  });
  const [refSettings, setRefSettings] = useState(null);

  useEffect(() => {
    // Load referral settings for the hook banner
    referralsAPI.publicSettings().then(setRefSettings).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user.role) {
      if (redirectTo) goAfterAuth();
      else if (user.role === 'admin' || user.role === 'founder') navigate('/admin');
      else navigate('/landing');
    }
  }, [user, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user && user.role) return null;

  const handleChange = (e) => {
    const val = e.target.name === 'referral_code' ? e.target.value.toUpperCase() : e.target.value;
    setForm(p => ({ ...p, [e.target.name]: val }));
    setError('');
  };

  const handleSubmit = async () => {
    setError('');
    if (!form.email || !form.password) { setError('Email and password required'); return; }
    if (mode === 'signup' && !form.name) { setError('Name required'); return; }
    setIsLoading(true);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
      } else {
        const payload = {
          name: form.name, email: form.email, password: form.password, phone: form.phone,
        };
        if (form.referral_code) payload.referral_code = form.referral_code;
        await register(payload);
      }
      goAfterAuth();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="customer-auth" className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#FAF9F6', fontFamily: 'Outfit, sans-serif' }}>
      <div className="sticky top-0 z-50 flex items-center justify-between px-4 py-4 border-b"
        style={{ backgroundColor: 'rgba(250,249,246,0.9)', backdropFilter: 'blur(12px)', borderColor: '#E5E5E5' }}>
        <button data-testid="back-btn" onClick={() => navigate('/landing')} className="p-2 -ml-2">
          <ChevronLeft className="w-6 h-6" style={{ color: '#0F0F0F' }} strokeWidth={1.5} />
        </button>
        <h1 className="text-lg" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>
          {mode === 'login' ? 'Sign In' : 'Create Account'}
        </h1>
        <div className="w-10" />
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 py-8">
        <div className="text-center mb-8">
          <img
            data-testid="customer-auth-logo"
            src="/lunora_logo.png"
            alt="Lunora's"
            className="w-28 h-auto mx-auto mb-3"
            style={{ display: 'block' }}
          />
          <h2 className="text-2xl mb-1" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>
            {mode === 'login' ? 'Welcome Back' : 'Join Lunora\'s'}
          </h2>
          <p className="text-sm" style={{ color: '#595959' }}>
            {mode === 'login' ? 'Sign in to continue' : 'Create your account'}
          </p>
          {['/measurements', '/my-fit-profile'].includes(redirectTo) && (
            <p data-testid="auth-scan-required-msg" className="text-xs mt-3 px-4 py-2 inline-block"
              style={{ color: '#7A5C12', backgroundColor: 'rgba(212,175,55,0.12)' }}>
              Please sign in with your account to start your AI Body Scan
            </p>
          )}
        </div>

        <div className="max-w-sm mx-auto w-full space-y-5">
          {mode === 'signup' && (
            <div>
              <label className="text-xs tracking-[0.15em] uppercase mb-2 block" style={{ color: '#595959' }}>Full Name</label>
              <div className="relative">
                <User className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#595959' }} strokeWidth={1.5} />
                <input data-testid="name-input" type="text" name="name" value={form.name} onChange={handleChange}
                  placeholder="Your name" className="w-full pl-8 py-3 text-sm bg-transparent border-0 border-b focus:outline-none"
                  style={{ borderColor: '#E5E5E5', color: '#0F0F0F' }} />
              </div>
            </div>
          )}

          <div>
            <label className="text-xs tracking-[0.15em] uppercase mb-2 block" style={{ color: '#595959' }}>Email</label>
            <div className="relative">
              <Mail className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#595959' }} strokeWidth={1.5} />
              <input data-testid="email-input" type="email" name="email" value={form.email} onChange={handleChange}
                placeholder="your@email.com" className="w-full pl-8 py-3 text-sm bg-transparent border-0 border-b focus:outline-none"
                style={{ borderColor: '#E5E5E5', color: '#0F0F0F' }} />
            </div>
          </div>

          <div>
            <label className="text-xs tracking-[0.15em] uppercase mb-2 block" style={{ color: '#595959' }}>Password</label>
            <div className="relative">
              <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#595959' }} strokeWidth={1.5} />
              <input data-testid="password-input" type={showPassword ? 'text' : 'password'} name="password" value={form.password} onChange={handleChange}
                placeholder="Enter password" className="w-full pl-8 pr-10 py-3 text-sm bg-transparent border-0 border-b focus:outline-none"
                style={{ borderColor: '#E5E5E5', color: '#0F0F0F' }} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-0 top-1/2 -translate-y-1/2 p-1">
                {showPassword ? <EyeOff className="w-5 h-5" style={{ color: '#595959' }} strokeWidth={1.5} /> : <Eye className="w-5 h-5" style={{ color: '#595959' }} strokeWidth={1.5} />}
              </button>
            </div>
            {mode === 'login' && (
              <div className="mt-2 text-right">
                <button type="button"
                  data-testid="forgot-password-link"
                  onClick={() => navigate('/forgot-password')}
                  className="text-[11px]" style={{ color: '#D4AF37' }}>
                  Forgot Password?
                </button>
              </div>
            )}
          </div>

          {mode === 'signup' && (
            <div>
              <label className="text-xs tracking-[0.15em] uppercase mb-2 block" style={{ color: '#595959' }}>Phone (Optional)</label>
              <div className="relative">
                <Phone className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#595959' }} strokeWidth={1.5} />
                <input data-testid="phone-input" type="tel" name="phone" value={form.phone} onChange={handleChange}
                  placeholder="Mobile number" maxLength={10} className="w-full pl-8 py-3 text-sm bg-transparent border-0 border-b focus:outline-none"
                  style={{ borderColor: '#E5E5E5', color: '#0F0F0F' }} />
              </div>
            </div>
          )}

          {mode === 'signup' && refSettings?.active && (
            <div>
              <label className="text-xs tracking-[0.15em] uppercase mb-2 block flex items-center gap-1.5" style={{ color: '#595959' }}>
                <Gift className="w-3 h-3" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
                Referral Code (Optional)
              </label>
              <div className="relative">
                <input data-testid="referral-code-input" type="text" name="referral_code" value={form.referral_code} onChange={handleChange}
                  placeholder="LUN-XXXX"
                  className="w-full py-3 text-sm bg-transparent border-0 border-b focus:outline-none uppercase tracking-wider"
                  style={{ borderColor: '#E5E5E5', color: '#0F0F0F' }} />
              </div>
              {form.referral_code && (
                <p className="text-[10px] mt-1" style={{ color: '#166534' }}>
                  ✓ You&apos;ll get ₹{refSettings.referee_reward} off your first order (min ₹{refSettings.min_order})
                </p>
              )}
            </div>
          )}

          {error && <p data-testid="auth-error" className="text-xs text-center" style={{ color: '#991B1B' }}>{error}</p>}

          <button data-testid="auth-submit-btn" onClick={handleSubmit} disabled={isLoading}
            className="w-full py-4 text-white text-sm tracking-[0.1em] uppercase disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)', fontWeight: 500 }}>
            {isLoading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>

          <div className="text-center">
            <button data-testid="toggle-mode-btn" onClick={() => { setMode(m => m === 'login' ? 'signup' : 'login'); setError(''); }}
              className="text-xs" style={{ color: '#595959' }}>
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <span style={{ color: '#D4AF37' }}>{mode === 'login' ? 'Sign Up' : 'Sign In'}</span>
            </button>
          </div>

          <div className="flex items-center justify-center gap-2 pt-2">
            <ShieldCheck className="w-4 h-4" style={{ color: '#166534' }} strokeWidth={1.5} />
            <span className="text-[10px]" style={{ color: '#595959' }}>Secure & Encrypted</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerAuth;
